import * as React from "react"
import {
  type Category,
  CategorySubtype,
  CategoryType,
  type EntityCategory,
} from "@digits-graphql/frontend/graphql-bearer"
import { type PrimitiveAtom, useAtom, useSetAtom } from "jotai"
import {
  InputGroup,
  PanelLabel,
} from "src/frontend/components/OS/Applications/CategoryManager/CategoryPanel/Shared"
import { CategoryTypeahead } from "src/shared/components/Typeahead/CategoryTypeahead"

const INCOME_CATEGORY_TYPES = [CategoryType.Income, CategoryType.OtherIncome]
const EXPENSE_CATEGORY_TYPES = [
  CategoryType.CostOfGoodsSold,
  CategoryType.Expenses,
  CategoryType.OtherExpenses,
]
const CATEGORY_TYPE_RESTRICTIONS_MAP = new Map<CategoryType, CategoryType[]>([
  [CategoryType.Income, INCOME_CATEGORY_TYPES],
  [CategoryType.OtherIncome, INCOME_CATEGORY_TYPES],
  [CategoryType.CostOfGoodsSold, EXPENSE_CATEGORY_TYPES],
  [CategoryType.Expenses, EXPENSE_CATEGORY_TYPES],
  [CategoryType.OtherExpenses, EXPENSE_CATEGORY_TYPES],
])

interface ParentCategorySelectProps {
  parentCategoryAtom: PrimitiveAtom<EntityCategory | undefined>
  subtypeAtom: PrimitiveAtom<CategorySubtype | undefined>
  restrictTypes?: boolean
  excludedCategoryIds?: string[]
}

export const ParentCategorySelect: React.FC<ParentCategorySelectProps> = ({
  parentCategoryAtom,
  subtypeAtom,
  restrictTypes,
  excludedCategoryIds,
}) => {
  const [parentCategory, setParentCategory] = useAtom(parentCategoryAtom)
  const setSubtype = useSetAtom(subtypeAtom)

  const onSelect = React.useCallback(
    (c: EntityCategory) => {
      setParentCategory(c)
      setSubtype(c.subtype === CategorySubtype.None ? undefined : c.subtype)
    },
    [setParentCategory, setSubtype]
  )

  const categoryDisplayNumber = useCategoryDisplayNumber(parentCategory)

  return (
    <InputGroup>
      <PanelLabel>Parent Category</PanelLabel>

      <CategoryTypeahead
        category={parentCategory}
        onSelect={onSelect}
        inputPlaceholder="Select parent…"
        excludedCategoryIds={excludedCategoryIds}
        excludedAncestorIds={excludedCategoryIds}
        includedTypes={
          restrictTypes && parentCategory
            ? CATEGORY_TYPE_RESTRICTIONS_MAP.get(parentCategory.type) || [parentCategory.type]
            : undefined
        }
        enableSelectRootCategory
        enableSelectParentCategory
        addNewBehavior="never"
        activateBehavior="select"
        tooltipDisplay="always"
        subtext={categoryDisplayNumber}
      />
    </InputGroup>
  )
}

const useCategoryDisplayNumber = (category: Category | EntityCategory | undefined) =>
  React.useMemo(() => {
    if (!category) return undefined

    if ((category as Category)?.displayNumber) {
      return (category as Category).displayNumber || undefined
    }

    if ((category as EntityCategory)?.chartOfAccountsDisplayNumber) {
      return (category as EntityCategory).chartOfAccountsDisplayNumber || undefined
    }

    return undefined
  }, [category])
